import config from '../config';
import content from '../content';
import { ApiError, ApiValidationError } from './api-error';
import { FormExpiredError } from './form-expired-error';
import { FormSubmittedError } from './form-submitted-error';
import { getTenantFromPath } from './tenant-utils';
import { createUrl } from './url-utils';

export interface ErrorResponse {
  errorType: string;
  errorMessage: string;
}

const getHeaderValue = (headers: HeadersInit | undefined, headerName: string): string => {
  if (!headers) return '';

  if (headers instanceof Headers) {
    return headers.get(headerName) || '';
  }

  if (Array.isArray(headers)) {
    const match = headers.find(([key]) => key.toLowerCase() === headerName.toLowerCase());
    return match?.[1] || '';
  }

  const recordHeaders = headers as Record<string, string | undefined>;
  const exact = recordHeaders[headerName];
  if (typeof exact === 'string') return exact;

  const lowercase = recordHeaders[headerName.toLowerCase()];
  return typeof lowercase === 'string' ? lowercase : '';
};

const withTenantHeader = (
  headers: HeadersInit | undefined,
  derivedTenant: string | null,
): Record<string, string> => {
  const merged = new Headers(headers || {});
  const explicitTenant = getHeaderValue(headers, 'X-Tenant').trim();
  const tenantToSend = explicitTenant || (derivedTenant || '').trim();

  if (tenantToSend) {
    merged.set('X-Tenant', tenantToSend);
  } else {
    merged.delete('X-Tenant');
    merged.delete('x-tenant');
  }

  return Object.fromEntries(merged.entries());
};

// Taken from backend/shared/aws/types.py
export const errorTypeMap = {
  unauthorized: 'unauthorized',
  unexpected: 'unexpected',
  formNotFound: 'formNotFound',
  formExpired: 'formExpired',
  formAlreadySubmitted: 'formAlreadySubmitted',
  invalidJsonBody: 'invalidJsonBody',
  saveAnswersFailed: 'saveAnswersFailed',
  saveStatusFailed: 'saveStatusFailed',
  noAnswersToSubmit: 'noAnswersToSubmit',
  formFailedValidation: 'formFailedValidation',
};

export const api = async (
  token: string,
  path: RequestInfo | URL,
  init: RequestInit = {},
  params?: Record<string, string | string[] | number>,
): Promise<Response> => {
  const tenant =
    typeof window !== 'undefined' ? getTenantFromPath(window.location?.pathname ?? '') : '';
  const url = createUrl(`${config.apiUrl}${path}`, params);
  let res: Response;

  try {
    const headers = withTenantHeader(init.headers, tenant);
    headers.Authorization = `Bearer ${token}`;

    // attempt fetch
    res = await fetch(url, {
      ...init,
      credentials: 'include',
      headers,
    });
    if (res.ok) return res;
  } catch (e) {
    // default Error
    throw new ApiError(content.errorApi);
  }

  // attempt to identify the error type
  // if not matched throw the default error
  const { errorMessage, errorType }: ErrorResponse = await res.json();
  if (errorType === errorTypeMap.formExpired) {
    throw new FormExpiredError(errorMessage);
  }
  if (errorType === errorTypeMap.formAlreadySubmitted) {
    throw new FormSubmittedError(errorMessage);
  }
  if (errorType === errorTypeMap.invalidJsonBody) {
    // Preserve backend message so pages can show it inline (e.g. email/domain validation).
    throw new ApiValidationError(errorMessage || content.errorApi, errorType);
  }
  if (
    errorType === errorTypeMap.unexpected &&
    typeof errorMessage === 'string' &&
    errorMessage.toLowerCase().includes('form is not awaiting approval')
  ) {
    throw new ApiError(content.approvalNoLongerAwaiting);
  }

  // default Error
  throw new ApiError(content.errorApi, errorMessage);
};

export const apiPublic = async (
  path: RequestInfo | URL,
  init: RequestInit = {},
  params?: Record<string, string | string[] | number>,
): Promise<Response> => {
  const tenant =
    typeof window !== 'undefined' ? getTenantFromPath(window.location?.pathname ?? '') : '';
  const url = createUrl(`${config.apiUrl}${path}`, params);

  try {
    const headers = withTenantHeader(init.headers, tenant);
    const res = await fetch(url, {
      ...init,
      credentials: 'include',
      headers,
    });

    if (res.ok) {
      return res;
    }

    let errorMessage: string = content.errorApi;
    try {
      const parsed = (await res.json()) as Partial<ErrorResponse>;
      if (typeof parsed.errorMessage === 'string' && parsed.errorMessage.trim()) {
        errorMessage = parsed.errorMessage;
      }
    } catch (_error) {
      // Ignore parse failures and fall back to default api message.
    }

    throw new ApiError(content.errorApi, errorMessage);
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(content.errorApi);
  }
};
